// @ts-nocheck

import logger from 'src/utils/logger';

import exenv from 'exenv';
import get from 'lodash/get';

// TODO (kyle): import these

/**
 * Attach a key-returning function to the given function. The key is used by
 * the cached and fetching decorators, and must be added *before* them.
 */
export function key(key: (...args: any[]) => unknown) {
  return (wrappedFunc: unknown) =>
    Object.assign(
      function (...params) {
        return Object.assign(wrappedFunc.apply(this, params), {
          KEY: () => key(...params),
        });
      },
      wrappedFunc,
      {KEY: key},
    );
}

/**
 * evaluates whether a Date.getTime() is older than a ttl
 */
const expired = (time: unknown, ttl: unknown) => {
  if (time) {
    const now = new Date().getTime();
    return now - time > ttl;
  }
  return true;
};

/**
 * Cache a promise-returning function.
 *
 * Handles resolving action calls that are already cached, and caching the
 * results of ones that aren't. Only works for promises
 */

// types for internal action creators used to populate cache

// These are defaults (i can't see why we would change them)
export const cacheSetAction = (
  key: unknown,
  timestamp = new Date().getTime(),
  queryHash: unknown,
) => ({type: '@@cache/set', payload: {key, timestamp, queryHash}});
const cacheHitAction = (key: unknown, ttl: unknown, queryHash: unknown) => ({
  type: '@@cache/hit',
  payload: {key, queryHash},
  meta: {ttl},
});
const cacheMissAction = (key: unknown, ttl: unknown, queryHash: unknown) => ({
  type: '@@cache/miss',
  payload: {key, queryHash},
  meta: {ttl},
});
const cacheTtlSelector = (state: unknown, key: unknown) =>
  get(state, ['requestCache', 'cache', key], {timestamp: 0});

// This is the type of our api methods now, they work this way so they can access the store
// and do any other validation needed before firing off an an xhr
// So while  reduxApi.get(path) returns => (dispatch, getState) => Promise
// you are welcome to take advantage of this by crafting an api call like
// userId => reduxApi.get(`path/${useId}`)

// TODO (kyle): use thunk action in reducer file

// The function that handles receipt of an api request is basically
// an action creator: feed it some payload value and get back an object
// with at least a string in the type field

// TODO (kyle): fix the typing on this decorator
export function cached(
  receive: (...args: any[]) => unknown,
  {
    ttl = 60000,
    hash,
    cacheSet = cacheSetAction,
    cacheHit = cacheHitAction,
    cacheMiss = cacheMissAction,
    ttlSelector = cacheTtlSelector,
  }: {
    ttl?: number;
    hash?: (...args: any[]) => string;
    cacheSet?: (key: unknown, timestamp: number, queryHash: unknown) => unknown;
    cacheHit?: (key: unknown, ttl: number, queryHash: unknown) => unknown;
    cacheMiss?: (key: unknown, ttl: number, queryHash: unknown) => unknown;
    ttlSelector?: (state: unknown, key: unknown) => unknown;
  } = {},
) {
  return (wrappedFunc: unknown) => {
    if (!wrappedFunc.KEY) {
      throw new Error('Must use key() decorator first before cached');
    }

    const cacheWrapped = (...restArgs) => {
      const thunk = wrappedFunc(...restArgs);
      const keyVal = thunk.KEY();

      const cacheThunk = function cacheThunk(
        dispatch: unknown,
        getState: unknown,
      ) {
        return new Promise((resolve: unknown, reject: unknown) => {
          const savedTtl = ttlSelector(getState(), keyVal);
          const isExpired = expired(savedTtl.timestamp, ttl);
          const queryHash = hash ? hash(...restArgs) : keyVal;

          if (!isExpired && queryHash === savedTtl.queryHash) {
            dispatch(cacheHit(keyVal, ttl, queryHash));
            resolve();
          } else {
            dispatch(cacheMiss(keyVal, ttl, queryHash));

            thunk(dispatch, getState)
              .then((result) => {
                dispatch(receive(result, ...restArgs));
                dispatch(cacheSet(keyVal, new Date().getTime(), queryHash));
                // TODO (kyle): why are we not resolving the result here?
                resolve();
              })
              .catch((error) => reject(error));
          }
        });
      };

      return Object.assign(cacheThunk, thunk);
    };

    return Object.assign(cacheWrapped, wrappedFunc);
  };
}

const selectInProgress = (state: unknown, key: unknown) =>
  get(state, ['requestCache', 'request', key, 'inflight']);
export const beginAction = (key: unknown, promise: unknown) => ({
  type: '@@requestCache/begin',
  payload: promise,
  meta: {key},
});
export const endAction = (key: unknown) => ({
  type: '@@requestCache/end',
  meta: {key},
});
export const errorAction = (key: unknown, error: unknown) => ({
  type: '@@requestCache/error',
  meta: {key},
  error,
});
export const progressAction = (key: unknown, progress: unknown) => ({
  type: '@@requestCache/progress',
  meta: {key},
  payload: progress,
});
/**
 * Batch an action and surface errors in global state.
 *
 * Handles preventing multiple concurrent calls to the same action by resolving
 * subsequent calls with the existing request promise. Puts any errors in the
 * global state so components can react to them.
 */

export function fetching({
  inProgressSelector = selectInProgress,
  beginRequestAction = beginAction,
  endRequestAction = endAction,
  errorRequestAction = errorAction,
} = {}) {
  return (wrappedFunc: unknown) => {
    if (!wrappedFunc.KEY) {
      throw new Error('Must use key() decorator first before fetching');
    }

    const fetchingWrapped = (...args) => {
      const thunk = wrappedFunc(...args);
      const keyValue = thunk.KEY();

      const fetchingThunk = function fetchingThunk(
        dispatch: unknown,
        getState: unknown,
      ) {
        const keyValue = wrappedFunc.KEY.apply(this, args);

        const inProgressRequest = inProgressSelector(getState(), keyValue);
        if (inProgressRequest) {
          return inProgressRequest;
        } else {
          // we could make this dispatch(wrappedFunc(args))
          // but that might tie us too closely to redux-thunk
          const request = thunk(dispatch, getState);
          dispatch(beginRequestAction(keyValue, request));

          // NOTE (kyle): we return the original request promise, not the
          // handled promise generated (and unassigned) here.
          request
            .then(() => {
              dispatch(endRequestAction(keyValue));
            })
            .catch((error) => {
              dispatch(errorRequestAction(keyValue, error));
            });

          return request;
        }
      };

      return Object.assign(fetchingThunk, thunk);
    };

    return Object.assign(fetchingWrapped, wrappedFunc);
  };
}

// check if a @key-ed actionCreator is still inflight
//     const pending = inflight(reduxState, getFoo()) // or
//     const stillWaiting = infilght(reduxState, getUser('userid'))
// the function call you feed to inflight should match the
// dependency your page or route or component needs
export function inflight(state: unknown, func: unknown) {
  if (!func.KEY) {
    throw new Error('only cached/fetching-wrapped actions have KEYs set');
  }
  return Boolean(
    get(state, ['requestCache', 'request', func.KEY() || '@@null', 'inflight']),
  );
}

export function hasError(state: unknown, func: unknown) {
  if (!func.KEY) {
    throw new Error('only cached/fetching-wrapped actions have KEYs set');
  }
  return get(state, ['requestCache', 'error', func.KEY()]);
}

export function progress(state: unknown, func: unknown) {
  if (!func.KEY) {
    throw new Error('only cached/fetching-wrapped actions have KEYs set');
  }
  return get(state, [
    'requestCache',
    'request',
    func.KEY() || '@@null',
    'progress',
  ]);
}

// evict an action creator's key to manually cache-bust
// dispatch this right before you dispatch an action which will receive
// possibly cached data
//     dispatch(evict(getFoo())) for arg-less getFoo or
//     dispatch(evict(getUser('someid')))
export const evictAction = (key: unknown) => ({
  type: '@@cache/evict',
  payload: {key},
});
export const evict = (thunk: unknown) => {
  if (!thunk.KEY) {
    throw new Error('only @key-ed action creators can be evicted');
  }
  return evictAction(thunk.KEY());
};
export const force = (thunk: unknown) => (dispatch: unknown) => {
  dispatch(evict(thunk));
  dispatch(thunk);
};

/**
 * Force an thunk to ignore caching rules and always hit the server.
 * Essentially the same as evicting before calling.
 *
 *   dispatch(forceThunk(getFoo()));
 */
export const forceThunk = (thunk: unknown) => (dispatch: unknown) => {
  dispatch(evict(thunk));
  dispatch(thunk);
};

/**
 * Redirect browser page to /signin if request throws Error: UNAUTHORIZED.
 */
export function authorized() {
  return (func: unknown) =>
    key(func.KEY)((...args) =>
      func.apply(this, args).catch((error) => {
        if (error.status && error.status === 401 && exenv.canUseDOM) {
          window.location = '/signin';
        }
        throw error;
      }),
    );
}

/**
 * Suppresses and logs request errors so that they don't break code
 * the react render cycle.
 */
export function suppressed() {
  return (func: unknown) =>
    key(func.KEY)((...args) =>
      func.apply(this, args).catch((error) => {
        const keyVal = func.KEY.apply(this, args);
        logger.error('error: ' + keyVal, error.stack || error);
      }),
    );
}

/**
 * Delays the thunk call by the specified miliseconds. Useful for testing
 * inflight booleans and such that would usually resolve nearly instantly.
 */
export function delay(ms: unknown) {
  return (func: unknown) =>
    key(func.KEY)(
      (...args) =>
        (dispatch: unknown, getState: unknown) =>
          new Promise((resolve: unknown) => {
            setTimeout(resolve, ms);
          }).then(() => func.apply(this, args)(dispatch, getState)),
    );
}
